import { BOOK_MODAL_SOURCE } from 'features/book_modal'
import { trackEvent, TRACKING_EVENT_NAMES } from 'hooks/analytics_events'

const isValidTrack = (track) => typeof track === 'function'

export const trackContactUpdatedEvent = (data, track) => {
  if (!isValidTrack(track)) {
    console.error('trackContactUpdatedEvent: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.CONTACT_UPDATED,
    properties: {
      update_type: data.updateType,
      contact_source: data.contactSource,
      contact_uuid: data.contactUuid
    }
  })
}

export const trackShareAvailabilityEvent = ({ contactUuid, source, step, track }) => {
  if (!isValidTrack(track)) {
    console.error('trackShareAvailabilityEvent: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
    properties: {
      contact_uuid: contactUuid,
      contact_source: source,
      step
    }
  })
}

export const trackScheduleInRealTimeEvent = ({ contactUuid, source, step, track }) => {
  if (!isValidTrack(track)) {
    console.error('trackScheduleInRealTimeEvent: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.SCHEDULE_IN_REAL_TIME,
    properties: {
      contact_uuid: contactUuid || null,
      contact_source: source,
      step
    }
  })
}

export const trackContactProfileView = ({ actionType, track }) => {
  if (!isValidTrack(track)) {
    console.error('trackContactProfileView: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.USER_ACTION_CAPTURED,
    properties: {
      action_name: 'contacts_profile_view',
      action_type: actionType,
      squad_ownership: 'invitee_experience',
      business_initiative: 'contacts_launch_tracking',
      capture_date_begin: new Date(2024, 2, 29),
      capture_date_end: new Date(2024, 11, 31)
    }
  })
}

export const trackInviteeBookedV3 = (
  dependencies = {},
  source = BOOK_MODAL_SOURCE.ET_CARD,
  {
    isReschedule = false,
    isOverrideAvailableTimes = false,
    location = null,
    userAction = null,
    bookingMethod = null,
  } = {},
) => {
  let resolvedBookingMethod = bookingMethod
  if (!resolvedBookingMethod) {
    if (isOverrideAvailableTimes) {
      resolvedBookingMethod = 'override_available_times'
    } else if (isReschedule) {
      resolvedBookingMethod = 'reschedule_available_times'
    } else {
      resolvedBookingMethod = 'schedule_in_real_time'
    }
  }

  const isBookOnBehalf = userAction === 'book_on_behalf'
  const isOwnerBooking = isReschedule || isBookOnBehalf

  let isRescheduleProperty = null
  if (isReschedule) {
    isRescheduleProperty = true
  } else if (isBookOnBehalf) {
    isRescheduleProperty = false
  }

  trackEvent({
    name: TRACKING_EVENT_NAMES.INVITEE_BOOKED,
    version: 3,
    dependencies,
    entities: ['organization', 'user', 'event', 'event_type', 'invitee'],
    properties: {
      location,
      user_action: userAction,
      booking_method: resolvedBookingMethod,
      share_attribution: source,
      redirect_type: 'none',
      is_test_booking: isOwnerBooking ? false : null,
      is_reschedule: isRescheduleProperty,
      is_owner_session: isOwnerBooking ? true : null,
      is_embedded: null,
      is_calendly_user: null,
      invitee_device: null,
      invitee_browser: null,
      booker_owner_org_match: null,
      is_override_available_times: isOverrideAvailableTimes,
    },
  })
}

export const trackFlowStartedEvent = ({ flowLocation, track }) => {
  if (!isValidTrack(track)) {
    console.error('trackFlowStartedEvent: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.FLOW_STARTED,
    properties: {
      flow_location: flowLocation,
      flow_name: 'schedule_in_real_time',
      flow_version: 'v1'
    }
  })
}

export const trackFlowStepCompleted = ({ track, flowLocation = 'contacts_page_right_rail_header' }) => {
  if (!isValidTrack(track)) {
    console.error('trackFlowStepCompleted: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.FLOW_STEP_COMPLETED,
    properties: {
      completion_method: 'step_completed',
      flow_name: 'schedule_in_real_time',
      flow_location: flowLocation,
      flow_step: 'select_et',
      flow_version: 'v1',
    }
  })
}

export const trackImportCsvFlowStepCompleted = ({ track }) => {
  if (!isValidTrack(track)) {
    console.error('trackImportCsvFlowStepCompleted: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.FLOW_STEP_COMPLETED,
    properties: {
      completion_method: 'step_completed',
      flow_name: 'import_from_csv',
      flow_step: 'select_csv',
      flow_location: 'contacts_home_header',
    },
  })
}

export const trackCRMInviteeBookedEvent = ({
  eventTypeUuid,
  eventUuid,
  inviteeUuid,
  userUuid,
  source = 'contacts_page_right_rail_header'
}) => (
  trackEvent({
    name: TRACKING_EVENT_NAMES.INVITEE_BOOKED,
    version: 3,
    dependencies: {
      event_type_uuid: eventTypeUuid,
      event_uuid: eventUuid,
      invitee_uuid: inviteeUuid,
      user_uuid: userUuid
    },
    entities: ['event_type', 'event', 'invitee', 'organization', 'user'],
    properties: {
      location: source,
      booking_method: 'schedule_in_real_time',
      booker_owner_org_match: null,
      invitee_browser: null,
      invitee_device: null,
      is_calendly_user: null,
      is_embedded: null,
      is_owner_session: null,
      is_reschedule: null,
      is_test_booking: null,
      redirect_type: null,
    }
  })
)

export const trackCRMFlowStepCompleted = ({
  flowLocation = 'contacts_page_right_rail_header',
  flowStep = 'select_et',
  eventTypeUuid,
  eventUuid,
  inviteeUuid,
  userUuid,
}) => {
  trackEvent({
    name: TRACKING_EVENT_NAMES.FLOW_STEP_COMPLETED,
    dependencies: {
      event_type_uuid: eventTypeUuid,
      event_uuid: eventUuid,
      invitee_uuid: inviteeUuid,
      user_uuid: userUuid
    },
    properties: {
      completion_method: 'step_completed',
      flow_name: 'schedule_in_real_time',
      flow_location: flowLocation,
      flow_step: flowStep,
      flow_version: 'v1',
    }
  })
}

export const trackContactInteraction = ({
  track,
  location,
  action,
  contactUuid,
}) => {
  if (!isValidTrack(track)) {
    console.error('trackContactInteraction: track must be a function')
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.CONTACT_INTERACTION,
    properties: {
      action,
      location,
      contact_uuid: contactUuid,
    }
  })
}
